import { supabase } from './supabase'
import { fetchAllPaged } from './pagination'
import { resolveAccountsByMappingKeys } from './accountingService'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, args: Record<string, unknown>): any => (supabase as any).rpc(fn, args)

const round2 = (n: number) => Math.round(n * 100) / 100

export type IncentiveStatus = 'draft' | 'posted' | 'voided'

export interface IncentivePeriod {
  id: string
  periodYear: number
  periodMonth: number
  baseRatePercent: number
  status: IncentiveStatus
  postedJournalId: string | null
  postedAt: string | null
  postedBy: string | null
  voidedAt: string | null
  voidedBy: string | null
  voidReason: string | null
  voidJournalId: string | null
  notes: string | null
}

export type RepresentativeKind = 'staff' | 'external'

export interface IncentivePeriodRep {
  representativeId: string | null
  representativeName: string | null
  representativeKind: RepresentativeKind
  overrideRatePercent: number | null
  calculatedIncentiveAmount: number | null
  snapshotSalesGross: number | null
  snapshotSalesCount: number | null
}

export interface PostIncentiveResult {
  posted: boolean
  total: number
  repCount: number
  repsSnapshotted: number
  journalId: string | null
  periodId: string
  entryDate: string | null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPeriod(row: any): IncentivePeriod {
  return {
    id: row.id,
    periodYear: row.period_year,
    periodMonth: row.period_month,
    baseRatePercent: Number(row.base_rate_percent ?? 0),
    status: (row.status ?? 'draft') as IncentiveStatus,
    postedJournalId: row.posted_journal_id ?? null,
    postedAt: row.posted_at ?? null,
    postedBy: row.posted_by ?? null,
    voidedAt: row.voided_at ?? null,
    voidedBy: row.voided_by ?? null,
    voidReason: row.void_reason ?? null,
    voidJournalId: row.void_journal_id ?? null,
    notes: row.notes ?? null,
  }
}

// Void-aware fetch. After mig 267 a month can hold BOTH a voided row and a fresh
// active (draft/posted) row — the partial unique index guarantees at most ONE
// active row. Return the active row if present, else the most recent voided row
// (so the page can show the voided state + a "Start new draft" affordance).
export async function fetchIncentivePeriod(
  tid: string,
  year: number,
  month: number,
): Promise<IncentivePeriod | null> {
  const { data, error } = await db('incentive_periods')
    .select('*')
    .eq('tenant_id', tid)
    .eq('period_year', year)
    .eq('period_month', month)
    .order('created_at', { ascending: false })
  if (error) throw new Error(error.message)
  const rows = (data ?? []).map(mapPeriod) as IncentivePeriod[]
  if (rows.length === 0) return null
  return rows.find(r => r.status !== 'voided') ?? rows[0]
}

// The single ACTIVE (non-voided) row for a month, or null. The partial unique
// index (mig 267) guarantees ≤1, so maybeSingle is safe.
async function fetchActiveIncentivePeriod(
  tid: string,
  year: number,
  month: number,
): Promise<IncentivePeriod | null> {
  const { data, error } = await db('incentive_periods')
    .select('*')
    .eq('tenant_id', tid)
    .eq('period_year', year)
    .eq('period_month', month)
    .neq('status', 'voided')
    .maybeSingle()
  if (error) throw new Error(error.message)
  return data ? mapPeriod(data) : null
}

export async function fetchIncentivePeriodReps(
  tid: string,
  periodId: string,
): Promise<IncentivePeriodRep[]> {
  const { data, error } = await db('incentive_period_reps')
    .select('representative_id, representative_name_snapshot, representative_kind, override_rate_percent, calculated_incentive_amount, snapshot_sales_gross, snapshot_sales_count')
    .eq('tenant_id', tid)
    .eq('incentive_period_id', periodId)
  if (error) throw new Error(error.message)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data ?? []).map((r: any): IncentivePeriodRep => ({
    representativeId: r.representative_id ?? null,
    representativeName: r.representative_name_snapshot ?? null,
    representativeKind: r.representative_kind === 'external' ? 'external' : 'staff',
    overrideRatePercent: r.override_rate_percent == null ? null : Number(r.override_rate_percent),
    calculatedIncentiveAmount: r.calculated_incentive_amount == null ? null : Number(r.calculated_incentive_amount),
    snapshotSalesGross: r.snapshot_sales_gross == null ? null : Number(r.snapshot_sales_gross),
    snapshotSalesCount: r.snapshot_sales_count == null ? null : Number(r.snapshot_sales_count),
  }))
}

export interface SaveIncentiveDraftInput {
  tenantId: string
  year: number
  month: number
  baseRatePercent: number
  overrides: {
    representativeId: string
    representativeName: string | null
    overrideRatePercent: number | null
  }[]
}

// Writes the ACTIVE period's base rate (creating the row on first save) plus the
// per-rep override rows. Only draft-editable fields are written — status /
// posted_* / snapshot columns are untouched. Returns the (updated|inserted) row.
//
// NOTE: mig 267 dropped the full (tenant,year,month) UNIQUE constraint for a
// partial index (active rows only), so we can no longer upsert on that target —
// instead we fetch the active row and UPDATE it, or INSERT a fresh draft. The
// mig 266 trigger is the server-side backstop against editing a non-draft period.
export async function saveIncentiveDraft(input: SaveIncentiveDraftInput): Promise<IncentivePeriod> {
  const active = await fetchActiveIncentivePeriod(input.tenantId, input.year, input.month)

  let period: IncentivePeriod
  if (active) {
    const { data, error } = await db('incentive_periods')
      .update({ base_rate_percent: input.baseRatePercent })
      .eq('id', active.id)
      .eq('tenant_id', input.tenantId)
      .select('*')
      .single()
    if (error) throw new Error(error.message)
    period = mapPeriod(data)
  } else {
    const { data, error } = await db('incentive_periods')
      .insert({
        tenant_id: input.tenantId,
        period_year: input.year,
        period_month: input.month,
        base_rate_percent: input.baseRatePercent,
      })
      .select('*')
      .single()
    if (error) throw new Error(error.message)
    period = mapPeriod(data)
  }

  if (input.overrides.length > 0) {
    const rows = input.overrides.map(o => ({
      tenant_id: input.tenantId,
      incentive_period_id: period.id,
      representative_id: o.representativeId,
      representative_name_snapshot: o.representativeName,
      override_rate_percent: o.overrideRatePercent,
    }))
    const { error: repsErr } = await db('incentive_period_reps')
      .upsert(rows, { onConflict: 'incentive_period_id,representative_id' })
    if (repsErr) throw new Error(repsErr.message)
  }

  return period
}

export async function postIncentiveAccrual(input: {
  tenantId: string
  periodId: string
  postedBy: string | null
}): Promise<PostIncentiveResult> {
  const { data, error } = await rpc('post_incentive_accrual', {
    p_tenant_id: input.tenantId,
    p_period_id: input.periodId,
    p_posted_by: input.postedBy,
  })
  if (error) throw new Error(error.message)
  const r = (data ?? {}) as Record<string, unknown>
  return {
    posted: !!r.posted,
    total: Number(r.total ?? 0),
    repCount: Number(r.rep_count ?? 0),
    repsSnapshotted: Number(r.reps_snapshotted ?? 0),
    journalId: (r.journal_id as string | null) ?? null,
    periodId: (r.period_id as string) ?? input.periodId,
    entryDate: (r.entry_date as string | null) ?? null,
  }
}

export interface VoidIncentiveResult {
  voided: boolean
  periodId: string
  originalJournalId: string | null
  voidJournalId: string | null
  totalReversed: number
  repCountReversed: number
  status: string
}

export async function voidIncentiveAccrual(input: {
  tenantId: string
  periodId: string
  voidReason: string
  voidedBy: string | null
}): Promise<VoidIncentiveResult> {
  const { data, error } = await rpc('void_incentive_accrual', {
    p_tenant_id: input.tenantId,
    p_period_id: input.periodId,
    p_void_reason: input.voidReason,
    p_voided_by: input.voidedBy,
  })
  if (error) throw new Error(error.message)
  const r = (data ?? {}) as Record<string, unknown>
  return {
    voided: !!r.voided,
    periodId: (r.period_id as string) ?? input.periodId,
    originalJournalId: (r.original_journal_id as string | null) ?? null,
    voidJournalId: (r.void_journal_id as string | null) ?? null,
    totalReversed: Number(r.total_reversed ?? 0),
    repCountReversed: Number(r.rep_count_reversed ?? 0),
    status: (r.status as string) ?? 'voided',
  }
}

// Starts a fresh draft for a month after the prior period was voided. A plain
// INSERT — the partial unique index (mig 267) rejects a second active row, and
// the mig 266 trigger allows draft INSERTs. Returns the new draft period.
export async function startNewIncentiveDraft(input: {
  tenantId: string
  year: number
  month: number
}): Promise<IncentivePeriod> {
  const { data, error } = await db('incentive_periods')
    .insert({
      tenant_id: input.tenantId,
      period_year: input.year,
      period_month: input.month,
      base_rate_percent: 0,
    })
    .select('*')
    .single()
  if (error) throw new Error(error.message)
  return mapPeriod(data)
}

// ─── B4: Incentive Payables (settlement) ──────────────────────────────────────

export interface IncentivePayableRow {
  repId: string
  repName: string
  totalAccrued: number   // net accruals (accrual credits − void-reversal debits)
  totalSettled: number   // real settlements only (source_type='incentive_settlement')
  outstanding: number    // net credit − debit over all 2205/staff lines for the rep
}

export interface IncentivePayablesResult {
  rows: IncentivePayableRow[]   // only outstanding > 0, sorted by outstanding desc
  totalOutstanding: number
  repCount: number
}

interface PayableLine {
  contact_id: string | null
  contact_name: string | null
  debit_amount: number | null
  credit_amount: number | null
  journal_entries: { source_type: string | null; is_voided: boolean | null } | null
}

// Per-rep incentive payable balance, COMPUTED FROM THE JOURNAL (CLAUDE.md #11) —
// there is no incentive_payables table. Sums Sales Incentive Payable (2205, via
// mapping_key) lines tagged contact_type='staff', bucketed by parent source_type
// so that void-reversals (which DEBIT 2205 with source_type='incentive_accrual')
// net a voided accrual to zero and are NOT reported as settlements. Paginated —
// 2205 accumulates 12 months × N reps + settlement lines and crosses PostgREST's
// 1000-row cap over time. Void-excluded via the !inner embed (belt-and-braces;
// incentive journals use reversed_by, not is_voided, and reversals net naturally).
export async function fetchIncentivePayables(tid: string): Promise<IncentivePayablesResult> {
  const acctIds = await resolveAccountsByMappingKeys(supabase, tid, ['sales_incentive_payable'])
  if (!acctIds.length) return { rows: [], totalOutstanding: 0, repCount: 0 }

  const lines = await fetchAllPaged<PayableLine>((from, to) =>
    db('journal_entry_lines')
      .select('contact_id, contact_name, debit_amount, credit_amount, journal_entries!inner(source_type, is_voided)')
      .eq('tenant_id', tid)
      .in('account_id', acctIds)
      .eq('contact_type', 'staff')
      .not('contact_id', 'is', null)
      .not('journal_entries.is_voided', 'is', true)
      .order('contact_id', { ascending: true })
      .order('id', { ascending: true })
      .range(from, to))

  interface Agg { repId: string; repName: string; accrued: number; settled: number; outstanding: number }
  const map = new Map<string, Agg>()
  for (const l of lines) {
    if (!l.contact_id) continue
    const d = Number(l.debit_amount ?? 0)
    const c = Number(l.credit_amount ?? 0)
    const src = l.journal_entries?.source_type ?? null
    let g = map.get(l.contact_id)
    if (!g) {
      g = { repId: l.contact_id, repName: l.contact_name ?? 'Unknown rep', accrued: 0, settled: 0, outstanding: 0 }
      map.set(l.contact_id, g)
    }
    if (l.contact_name) g.repName = l.contact_name
    if (src === 'incentive_accrual') g.accrued += c - d
    if (src === 'incentive_settlement') g.settled += d
    g.outstanding += c - d
  }

  const rows: IncentivePayableRow[] = [...map.values()]
    .map(g => ({
      repId: g.repId,
      repName: g.repName,
      totalAccrued: round2(g.accrued),
      totalSettled: round2(g.settled),
      outstanding: round2(g.outstanding),
    }))
    .filter(r => r.outstanding > 0.005)
    .sort((a, b) => b.outstanding - a.outstanding)

  return {
    rows,
    totalOutstanding: round2(rows.reduce((s, r) => s + r.outstanding, 0)),
    repCount: rows.length,
  }
}

export interface SettleIncentiveResult {
  settled: boolean
  representativeId: string
  repName: string
  amount: number
  journalId: string | null
  entryDate: string | null
  totalAccrued: number
  totalSettled: number
  newBalance: number
}

export async function settleIncentivePayable(input: {
  tenantId: string
  representativeId: string
  amount: number
  paymentAccountId: string
  settledBy: string | null
  note?: string | null
}): Promise<SettleIncentiveResult> {
  const { data, error } = await rpc('settle_incentive_payable', {
    p_tenant_id: input.tenantId,
    p_representative_id: input.representativeId,
    p_amount: input.amount,
    p_payment_account_id: input.paymentAccountId,
    p_settled_by: input.settledBy,
    p_note: input.note ?? null,
  })
  if (error) throw new Error(error.message)
  const r = (data ?? {}) as Record<string, unknown>
  return {
    settled: !!r.settled,
    representativeId: (r.representative_id as string) ?? input.representativeId,
    repName: (r.rep_name as string) ?? '',
    amount: Number(r.amount ?? 0),
    journalId: (r.journal_id as string | null) ?? null,
    entryDate: (r.entry_date as string | null) ?? null,
    totalAccrued: Number(r.total_accrued ?? 0),
    totalSettled: Number(r.total_settled ?? 0),
    newBalance: Number(r.new_balance ?? 0),
  }
}
